/**
 * StateManager.ts - Scenario state management for DEFCON TERMINAL
 *
 * Manages the dynamic state variables during scenario gameplay including
 * DEFCON level, world tension, morale, and custom variables.
 */

import type { ScenarioState, StateChange } from '@shared/types';
import { EventEmitter, type EventMap } from './EventEmitter';

/**
 * State bounds configuration
 */
export const STATE_BOUNDS = {
  defcon: { min: 1, max: 5 },
  worldTension: { min: 0, max: 100 },
  alliedMorale: { min: 0, max: 100 },
  sovietAggression: { min: 0, max: 100 },
} as const;

/**
 * Events emitted by the StateManager
 */
export interface StateManagerEvents extends EventMap {
  'state:change': {
    previous: ScenarioState;
    current: ScenarioState;
    changes: StateChange;
  };
  'defcon:change': {
    previous: number;
    current: number;
  };
  'state:warning': {
    field: string;
    message: string;
    value: number;
  };
}

/**
 * Parses a relative value string (e.g., "+5", "-10") or returns absolute value
 *
 * @param value - String or number value
 * @param currentValue - Current value to apply relative change to
 * @returns Calculated numeric value
 */
export function parseRelativeValue(
  value: string | number,
  currentValue: number
): number {
  if (typeof value === 'number') {
    return value;
  }

  const trimmed = value.trim();

  // Handle relative changes
  if (trimmed.startsWith('+')) {
    const delta = parseFloat(trimmed.slice(1));
    if (!isNaN(delta)) {
      return currentValue + delta;
    }
  } else if (trimmed.startsWith('-')) {
    const delta = parseFloat(trimmed);
    if (!isNaN(delta)) {
      return currentValue + delta; // delta is already negative
    }
  }

  // Try parsing as absolute value
  const absolute = parseFloat(trimmed);
  if (!isNaN(absolute)) {
    return absolute;
  }

  // Return current value if parsing fails
  console.warn(`Failed to parse state value: "${value}"`);
  return currentValue;
}

/**
 * Parses a relative time string (e.g., "+2h", "+30m")
 *
 * @param timeChange - Relative time change string
 * @param currentTime - Current time in HH:MM:SSZ format
 * @returns New time string
 */
export function parseRelativeTime(
  timeChange: string,
  currentTime: string
): string {
  // Parse current time
  const match = currentTime.match(/^(\d{2}):(\d{2}):(\d{2})Z?$/);
  if (!match) {
    console.warn(`Invalid time format: "${currentTime}"`);
    return currentTime;
  }

  let hours = parseInt(match[1], 10);
  let minutes = parseInt(match[2], 10);
  let seconds = parseInt(match[3], 10);

  // Parse time change
  const changeMatch = timeChange.match(/^([+-])(\d+)([hms])$/);
  if (!changeMatch) {
    console.warn(`Invalid time change format: "${timeChange}"`);
    return currentTime;
  }

  const sign = changeMatch[1] === '+' ? 1 : -1;
  const amount = parseInt(changeMatch[2], 10);
  const unit = changeMatch[3];

  // Apply change
  switch (unit) {
    case 'h':
      hours += sign * amount;
      break;
    case 'm':
      minutes += sign * amount;
      break;
    case 's':
      seconds += sign * amount;
      break;
  }

  // Normalize time values
  while (seconds < 0) {
    seconds += 60;
    minutes -= 1;
  }
  while (seconds >= 60) {
    seconds -= 60;
    minutes += 1;
  }
  while (minutes < 0) {
    minutes += 60;
    hours -= 1;
  }
  while (minutes >= 60) {
    minutes -= 60;
    hours += 1;
  }
  while (hours < 0) {
    hours += 24;
  }
  while (hours >= 24) {
    hours -= 24;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}Z`;
}

/**
 * Parses a relative date string (e.g., "+1d", "-2d")
 *
 * @param dateChange - Relative date change string
 * @param currentDate - Current date in YYYY-MM-DD format
 * @returns New date string
 */
export function parseRelativeDate(
  dateChange: string,
  currentDate: string
): string {
  // Parse date change
  const changeMatch = dateChange.match(/^([+-])(\d+)d$/);
  if (!changeMatch) {
    console.warn(`Invalid date change format: "${dateChange}"`);
    return currentDate;
  }

  const sign = changeMatch[1] === '+' ? 1 : -1;
  const days = parseInt(changeMatch[2], 10);

  // Parse and modify date
  const date = new Date(currentDate);
  if (isNaN(date.getTime())) {
    console.warn(`Invalid date format: "${currentDate}"`);
    return currentDate;
  }

  date.setDate(date.getDate() + sign * days);

  // Format as YYYY-MM-DD
  return date.toISOString().split('T')[0];
}

/**
 * Clamps a value within specified bounds
 */
export function clampValue(
  value: number,
  min: number,
  max: number
): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * StateManager class - Manages scenario state during gameplay
 */
export class StateManager extends EventEmitter<StateManagerEvents> {
  // Cap history to prevent unbounded memory growth
  private static readonly MAX_HISTORY = 50;

  private state: ScenarioState;
  private initialState: ScenarioState;
  private history: ScenarioState[] = [];

  constructor(initialState?: ScenarioState) {
    super();
    this.initialState = initialState
      ? this.cloneState(initialState)
      : this.createDefaultState();
    this.state = this.cloneState(this.initialState);
  }

  /**
   * Creates a default state object
   */
  private createDefaultState(): ScenarioState {
    return {
      defcon: 3,
      worldTension: 50,
      alliedMorale: 50,
      sovietAggression: 50,
      date: '1984-06-15',
      time: '12:00:00Z',
      customVariables: {},
    };
  }

  /**
   * Creates a deep clone of a state object
   */
  private cloneState(state: ScenarioState): ScenarioState {
    return {
      ...state,
      customVariables: state.customVariables
        ? { ...state.customVariables }
        : {},
    };
  }

  /**
   * Initialize state from a scenario's initial state
   *
   * @param initialState - Initial state from scenario
   */
  initialize(initialState: ScenarioState): void {
    this.initialState = this.cloneState(initialState);
    this.state = this.cloneState(initialState);
    this.history = [];
  }

  /**
   * Get the current state
   */
  getState(): ScenarioState {
    return this.cloneState(this.state);
  }

  /**
   * Get a snapshot of current state for history tracking
   */
  getSnapshot(): ScenarioState {
    return this.cloneState(this.state);
  }

  /**
   * Get the state history
   */
  getHistory(): ScenarioState[] {
    return this.history.map((s) => this.cloneState(s));
  }

  /**
   * Get a specific state value
   */
  getValue<K extends keyof ScenarioState>(key: K): ScenarioState[K] {
    return this.state[key];
  }

  /**
   * Get a custom variable value
   */
  getCustomVariable(name: string): number | string | boolean | undefined {
    return this.state.customVariables?.[name];
  }

  /**
   * Check if a custom variable exists
   */
  hasCustomVariable(name: string): boolean {
    return this.state.customVariables?.[name] !== undefined;
  }

  /**
   * Apply state changes from a choice
   *
   * @param changes - State changes to apply
   */
  applyChanges(changes: StateChange): void {
    // Save current state to history
    this.history.push(this.cloneState(this.state));

    // Evict oldest entry when history exceeds cap
    if (this.history.length > StateManager.MAX_HISTORY) {
      this.history.shift();
    }

    const previous = this.cloneState(this.state);
    const previousDefcon = this.state.defcon;

    // Apply defcon change
    if (changes.defcon !== undefined) {
      const newDefcon = parseRelativeValue(changes.defcon, this.state.defcon);
      this.state.defcon = clampValue(
        Math.round(newDefcon),
        STATE_BOUNDS.defcon.min,
        STATE_BOUNDS.defcon.max
      );
    }

    // Apply world tension change
    if (changes.worldTension !== undefined) {
      const newTension = parseRelativeValue(
        changes.worldTension,
        this.state.worldTension
      );
      this.state.worldTension = clampValue(
        Math.round(newTension),
        STATE_BOUNDS.worldTension.min,
        STATE_BOUNDS.worldTension.max
      );
    }

    // Apply allied morale change
    if (changes.alliedMorale !== undefined) {
      const newMorale = parseRelativeValue(
        changes.alliedMorale,
        this.state.alliedMorale
      );
      this.state.alliedMorale = clampValue(
        Math.round(newMorale),
        STATE_BOUNDS.alliedMorale.min,
        STATE_BOUNDS.alliedMorale.max
      );
    }

    // Apply soviet aggression change
    if (changes.sovietAggression !== undefined) {
      const newAggression = parseRelativeValue(
        changes.sovietAggression,
        this.state.sovietAggression
      );
      this.state.sovietAggression = clampValue(
        Math.round(newAggression),
        STATE_BOUNDS.sovietAggression.min,
        STATE_BOUNDS.sovietAggression.max
      );
    }

    // Apply time change
    if (changes.time !== undefined) {
      this.state.time = parseRelativeTime(changes.time, this.state.time);
    }

    // Apply date change
    if (changes.date !== undefined) {
      this.state.date = parseRelativeDate(changes.date, this.state.date);
    }

    // Apply custom variables
    if (changes.customVariables) {
      if (!this.state.customVariables) {
        this.state.customVariables = {};
      }

      for (const [key, value] of Object.entries(changes.customVariables)) {
        if (typeof value === 'number') {
          // Check if it should be relative (stored as string in the original)
          const existing = this.state.customVariables[key];
          if (typeof existing === 'number') {
            this.state.customVariables[key] = value;
          } else {
            this.state.customVariables[key] = value;
          }
        } else {
          this.state.customVariables[key] = value;
        }
      }
    }

    // Emit state change event
    this.emit('state:change', {
      previous,
      current: this.cloneState(this.state),
      changes,
    });

    // Emit defcon change event if defcon changed
    if (this.state.defcon !== previousDefcon) {
      this.emit('defcon:change', {
        previous: previousDefcon,
        current: this.state.defcon,
      });
    }

    // Emit warnings for critical state values
    if (this.state.defcon <= 2) {
      this.emit('state:warning', {
        field: 'defcon',
        message: 'DEFCON level critical - nuclear response imminent',
        value: this.state.defcon,
      });
    }

    if (this.state.worldTension >= 90) {
      this.emit('state:warning', {
        field: 'worldTension',
        message: 'World tension at critical levels',
        value: this.state.worldTension,
      });
    }

    if (this.state.alliedMorale <= 10) {
      this.emit('state:warning', {
        field: 'alliedMorale',
        message: 'Allied morale critically low',
        value: this.state.alliedMorale,
      });
    }
  }

  /**
   * Set a custom variable directly
   *
   * @param name - Variable name
   * @param value - Variable value
   */
  setCustomVariable(
    name: string,
    value: number | string | boolean
  ): void {
    if (!this.state.customVariables) {
      this.state.customVariables = {};
    }
    this.state.customVariables[name] = value;
  }

  /**
   * Restore state to a specific history index
   *
   * @param index - History index to restore to
   */
  restoreToHistory(index: number): void {
    if (index < 0 || index >= this.history.length) {
      console.warn(`Invalid history index: ${index}`);
      return;
    }

    const previous = this.cloneState(this.state);
    this.state = this.cloneState(this.history[index]);
    this.history = this.history.slice(0, index);

    this.emit('state:change', {
      previous,
      current: this.cloneState(this.state),
      changes: {},
    });
  }

  /**
   * Reset state to initial values
   */
  reset(): void {
    const previous = this.cloneState(this.state);
    this.state = this.cloneState(this.initialState);
    this.history = [];

    this.emit('state:change', {
      previous,
      current: this.cloneState(this.state),
      changes: {},
    });
  }

  /**
   * Check if DEFCON is at critical level (1 or 2)
   */
  isDefconCritical(): boolean {
    return this.state.defcon <= 2;
  }

  /**
   * Check if world tension is high (above threshold)
   *
   * @param threshold - Tension threshold (default 75)
   */
  isTensionHigh(threshold: number = 75): boolean {
    return this.state.worldTension >= threshold;
  }

  /**
   * Evaluate a condition string against current state
   *
   * @param condition - Condition string (e.g., "defcon <= 3", "worldTension > 50")
   * @returns True if condition is met
   */
  evaluateCondition(condition: string): boolean {
    try {
      // Parse simple conditions like "defcon <= 3" or "customVar == true"
      const match = condition.match(
        /^(\w+)\s*(==|!=|<=|>=|<|>)\s*(.+)$/
      );

      if (!match) {
        console.warn(`Invalid condition format: "${condition}"`);
        return false;
      }

      const [, variable, operator, valueStr] = match;
      let currentValue: number | string | boolean | undefined;

      // Get current value based on variable name
      if (variable in this.state && variable !== 'customVariables') {
        currentValue = this.state[variable as keyof ScenarioState] as
          | number
          | string
          | boolean;
      } else if (this.state.customVariables?.[variable] !== undefined) {
        currentValue = this.state.customVariables[variable];
      } else {
        console.warn(`Unknown variable in condition: "${variable}"`);
        return false;
      }

      // Parse comparison value
      let compareValue: number | string | boolean;
      if (valueStr === 'true') {
        compareValue = true;
      } else if (valueStr === 'false') {
        compareValue = false;
      } else if (!isNaN(parseFloat(valueStr))) {
        compareValue = parseFloat(valueStr);
      } else {
        // String value (strip quotes if present)
        compareValue = valueStr.replace(/^["']|["']$/g, '');
      }

      // Perform comparison
      switch (operator) {
        case '==':
          return currentValue === compareValue;
        case '!=':
          return currentValue !== compareValue;
        case '<':
          return (currentValue as number) < (compareValue as number);
        case '>':
          return (currentValue as number) > (compareValue as number);
        case '<=':
          return (currentValue as number) <= (compareValue as number);
        case '>=':
          return (currentValue as number) >= (compareValue as number);
        default:
          return false;
      }
    } catch (error) {
      console.error(`Error evaluating condition "${condition}":`, error);
      return false;
    }
  }

  /**
   * Export state for serialization
   */
  toJSON(): ScenarioState {
    return this.cloneState(this.state);
  }

  /**
   * Import state from serialized data
   */
  fromJSON(data: ScenarioState): void {
    const previous = this.cloneState(this.state);
    this.state = this.cloneState(data);

    this.emit('state:change', {
      previous,
      current: this.cloneState(this.state),
      changes: {},
    });
  }
}
